import React, { useEffect, useRef, useState } from "react"

import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { useMediaQuery } from "usehooks-ts"

import { fetchData, shouldReduceMotion } from "@utils"

import { Project } from "../types"
import ProjectCard from "./project-card"

const GITHUB_USERNAME = "aashutoshrathi"
const PROJECTS_URL = `https://api.github.com/users/${GITHUB_USERNAME}/repos?per_page=100&type=owner&sort=pushed&direction=desc`
const MAX_PROJECTS = 10
const CACHE_KEY = "github-projects"

/* Repos that shouldn't show up as projects (e.g. the profile README) */
const EXCLUDED_REPOS = new Set([GITHUB_USERNAME, "homebrew-tap"])

const loadProjectsCache = (): Project[] | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed as Project[]
    }
  } catch {}
  return null
}

const saveProjectsCache = (data: Project[]) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch {}
}

type ProjectSort = "recent" | "stars"

const SORT_OPTIONS: { value: ProjectSort; label: string }[] = [
  { value: "recent", label: "Recent" },
  { value: "stars", label: "Most starred" },
]

const filterProjects = (repos: Project[]): Project[] =>
  repos.filter(
    (repo) =>
      !repo.fork &&
      !repo.archived &&
      repo.description &&
      !EXCLUDED_REPOS.has(repo.name)
  )

const sortProjects = (repos: Project[], sortBy: ProjectSort): Project[] => {
  if (sortBy === "stars") {
    return [...repos].sort(
      (a, b) => (b.stargazers_count ?? 0) - (a.stargazers_count ?? 0)
    )
  }
  return repos // API already returns most recently pushed first
}

const DUMMY_PROJECTS: Project[] = [
  {
    id: 1430148210,
    name: "quietest",
    description:
      "Mute the tests in GitHub PRs. Review the code that matters - a Chrome extension that hides test files, mocks and snapshots until you ask.",
    language: "JavaScript",
    html_url: "https://github.com/aashutoshrathi/quietest",
    homepage: "https://quietest.aashutosh.dev/",
    stargazers_count: 2,
  },
  {
    id: 1343990722,
    name: "mnm",
    description:
      "Marker & Mayhem - an online Pictionary host. Everyone joins from their own phone, both teams draw the same word at the same time, with clock and tally built in.",
    language: "JavaScript",
    html_url: "https://github.com/aashutoshrathi/mnm",
    homepage: "https://mnm.aashutosh.dev/",
    stargazers_count: 0,
  },
  {
    id: 1277131490,
    name: "toki",
    description:
      "Native macOS menu bar app for managing Claude Code and Codex usage, sessions, and remote control via Homebrew.",
    language: "Swift",
    html_url: "https://github.com/aashutoshrathi/toki",
    homepage: "https://toki.aashutosh.dev/",
    stargazers_count: 21,
  },
  {
    id: 105375545,
    name: "Testcase-Generator",
    description:
      "⚡️ Handy script for HackerRank, HackerEarth and CodeChef TCs generation - paste once, get edge cases for free.",
    language: "Python",
    html_url: "https://github.com/aashutoshrathi/Testcase-Generator",
    homepage: "https://tcgen.aashutosh.dev/",
    stargazers_count: 110,
  },
  {
    id: 236274583,
    name: "aashutosh.dev",
    description:
      "My digital garden - Gatsby + Tailwind + GSAP, writing about automation, tools, and life in between.",
    language: "MDX",
    html_url: "https://github.com/aashutoshrathi/aashutosh.dev",
    homepage: "https://aashutosh.dev",
    stargazers_count: 5,
  },
  {
    id: 115751881,
    name: "Insta-Downloader-Extension",
    description:
      "Browser extension that injects download buttons ⬇️ for media on Instagram Web",
    language: "JavaScript",
    html_url: "https://github.com/aashutoshrathi/Insta-Downloader-Extension",
    homepage:
      "https://addons.mozilla.org/en-US/firefox/addon/instagram-media-downloader/",
    stargazers_count: 82,
  },
  {
    id: 150223916,
    name: "git-profiler-bot",
    description:
      "Telegram bot that fetches GitHub profiles on the fly - quick lookups without leaving chat.",
    language: "Python",
    html_url: "https://github.com/aashutoshrathi/git-profiler-bot",
    homepage: "http://t.me/git_profile_bot",
    stargazers_count: 26,
  },
  {
    id: 999999001,
    name: "dig",
    description: "Search companion for nibbles.dev - dig through bite-sized notes, fast and offline-friendly.",
    language: "TypeScript",
    html_url: "https://github.com/the-nibble/dig",
    homepage: "https://dig.nibbles.dev",
    stargazers_count: 0,
  },
]

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [sortBy, setSortBy] = useState<ProjectSort>("recent")
  const projectsRef = useRef<HTMLElement | null>(null)
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const displayedProjects = projects
    ? sortProjects(projects, sortBy).slice(0, MAX_PROJECTS)
    : null

  useGSAP(
    () => {
      if (shouldReduceMotion()) return
      const tl = gsap.timeline()

      if (projectsRef.current) {
        tl.fromTo(
          projectsRef.current.children,
          {
            x: (index) => (isDesktop ? (index % 2 === 0 ? -24 : 24) : 0),
            y: isDesktop ? 0 : -12,
            opacity: 0,
          },
          {
            x: 0,
            y: 0,
            opacity: 1,
            stagger: 0.04,
            ease: "expo.in",
            duration: 0.4,
          }
        )
      }
    },
    { dependencies: [projects?.length] }
  )

  useEffect(() => {
    const cached = loadProjectsCache()
    if (cached) setProjects(cached)

    fetchData<Project[]>(PROJECTS_URL)
      .then((repos) => {
        const filtered = filterProjects(repos)
        setProjects(filtered)
        saveProjectsCache(filtered)
      })
      .catch(() => {
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch projects, using dummy data.")
        }
        if (!cached) setProjects(DUMMY_PROJECTS)
      })
  }, [])

  return displayedProjects ? (
    <>
      <div
        className="mt-2 flex justify-end gap-1 rounded-lg"
        role="group"
        aria-label="Sort projects">
        {SORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setSortBy(option.value)}
            aria-pressed={sortBy === option.value}
            className={`rounded-lg px-3 py-1.5 text-sm transition-colors duration-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-700 dark:focus-visible:ring-blue-300 ${
              sortBy === option.value
                ? "bg-blue-600 font-semibold text-white dark:bg-blue-400 dark:text-slate-900"
                : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-slate-800"
            }`}>
            {option.label}
          </button>
        ))}
      </div>
      <section ref={projectsRef} className="mt-4 grid md:grid-cols-2 gap-6">
        {displayedProjects.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </section>
    </>
  ) : (
    <div
      className="mt-16 flex justify-center gap-2"
      role="status"
      aria-label="Loading projects">
      <div className="size-3 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.32s] dark:bg-slate-200" />
      <div className="size-3 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.16s] dark:bg-slate-200" />
      <div className="size-3 animate-bounce rounded-full bg-slate-400 dark:bg-slate-200" />
      <span className="sr-only">Loading projects…</span>
    </div>
  )
}

export default Projects
